import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { toast } from '../utils/toast';
import { Plus, Edit, Trash2, Search, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuestionBankPageNew = () => {
  const navigate = useNavigate();
  
  // Data states
  const [questions, setQuestions] = useState([]);
  const [trainingTopics, setTrainingTopics] = useState([]);
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filter states
  const [topicFilter, setTopicFilter] = useState('');
  const [sectionFilter, setSectionFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  // Language filter removed as per requirement
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  
  // Form states for Add/Edit
  const [formStep, setFormStep] = useState(1);
  const [selectedTopic, setSelectedTopic] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [questionText, setQuestionText] = useState('');
  const [options, setOptions] = useState([
    { text: '', is_correct: false },
    { text: '', is_correct: false }
  ]);
  const [answerType, setAnswerType] = useState('mcq_single');
  const [score, setScore] = useState(1);
  const [primaryLanguage, setPrimaryLanguage] = useState('English');
  const [primaryLanguageSearch, setPrimaryLanguageSearch] = useState('');
  const [showPrimaryLanguageDropdown, setShowPrimaryLanguageDropdown] = useState(false);
  const [secondaryLanguages, setSecondaryLanguages] = useState([]);
  // Structure: [{ language: 'Hindi', questionText: '', options: [...] }]

  useEffect(() => {
    fetchLanguages();
    fetchData();
  }, []);

  const fetchLanguages = async () => {
    try {
      const response = await axios.get(`${API}/training/languages`);
      setAvailableLanguages(response.data.languages || []);
    } catch (error) {
      console.error('Failed to fetch languages:', error);
      // Fallback to default languages
      setAvailableLanguages(['English', 'Hindi', 'Kannada', 'Tamil', 'Telugu', 'Marathi', 'Bengali', 'Gujarati', 'Malayalam', 'Punjabi']);
    }
  };

  // Reset section when topic changes
  useEffect(() => {
    if (selectedTopic) {
      setSelectedSection('');
      console.log('Topic changed, resetting section');
    }
  }, [selectedTopic]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Fetch questions
      const questionsRes = await axios.get(`${API}/assessments/questions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Backend returns {questions: [...], count: number}
      const questionsData = questionsRes.data.questions || questionsRes.data;
      setQuestions(Array.isArray(questionsData) ? questionsData : []);
      console.log('Fetched questions count:', questionsData.length);
      console.log('First question sample:', questionsData[0]);
      console.log('Question IDs:', questionsData.map(q => q.question_id));
      
      // Fetch training chapters for filtering
      const topicsRes = await axios.get(`${API}/training/content`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrainingTopics(Array.isArray(topicsRes.data) ? topicsRes.data : []);
    } catch (error) {
      toast.error('Failed to fetch data');
      console.error('Fetch error:', error);
      setQuestions([]);
      setTrainingTopics([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddOption = () => {
    if (options.length < 6) {
      setOptions([...options, { text: '', is_correct: false }]);
      // Sync all secondary languages
      setSecondaryLanguages(secondaryLanguages.map(secLang => ({
        ...secLang,
        options: [...secLang.options, { text: '', is_correct: false }]
      })));
    }
  };

  const handleRemoveOption = (index) => {
    if (options.length > 2) {
      setOptions(options.filter((_, i) => i !== index));
      // Sync all secondary languages
      setSecondaryLanguages(secondaryLanguages.map(secLang => ({
        ...secLang,
        options: secLang.options.filter((_, i) => i !== index)
      })));
    } else {
      toast.error('At least 2 options required');
    }
  };

  const handleOptionChange = (index, field, value) => {
    const newOptions = [...options];
    if (field === 'is_correct') {
      if (answerType === 'mcq_single') {
        // Single correct - uncheck all others
        newOptions.forEach((opt, i) => {
          opt.is_correct = i === index ? value : false;
        });
      } else {
        // Multiple correct
        newOptions[index].is_correct = value;
      }
      
      // Sync correct answers to all secondary languages
      setSecondaryLanguages(secondaryLanguages.map(secLang => {
        const updatedOptions = secLang.options.map((opt, i) => ({
          ...opt,
          is_correct: newOptions[i]?.is_correct || false
        }));
        return { ...secLang, options: updatedOptions };
      }));
    } else {
      newOptions[index][field] = value;
    }
    setOptions(newOptions);
  };

  const resetForm = () => {
    setFormStep(1);
    setSelectedTopic('');
    setSelectedSection('');
    setQuestionText('');
    setOptions([
      { text: '', is_correct: false },
      { text: '', is_correct: false }
    ]);
    setAnswerType('mcq_single');
    setScore(1);
    setPrimaryLanguage('English');
    setSecondaryLanguages([]);
    setEditingQuestion(null);
  };

  const validateForm = () => {
    if (!selectedTopic) {
      toast.error('Please select a chapter');
      return false;
    }
    if (!selectedSection) {
      toast.error('Please select a subchapter');
      return false;
    }
    if (!questionText.trim()) {
      toast.error('Please enter question text');
      return false;
    }
    if (options.some(opt => !opt.text.trim())) {
      toast.error('All options must have text');
      return false;
    }
    if (!options.some(opt => opt.is_correct)) {
      toast.error('Please mark at least one correct answer');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      const token = localStorage.getItem('token');
      
      // Build translations object
      const translationsObj = {
        [primaryLanguage]: {
          question_text: questionText,
          options: options.map(opt => ({ text: opt.text, is_correct: opt.is_correct }))
        }
      };
      
      // Add all secondary languages
      secondaryLanguages.forEach(secLang => {
        if (secLang.language && secLang.questionText.trim()) {
          translationsObj[secLang.language] = {
            question_text: secLang.questionText,
            options: secLang.options.map(opt => ({ text: opt.text, is_correct: opt.is_correct }))
          };
        }
      });
      
      const payload = {
        question_text: questionText,
        question_type: answerType,
        options: options.map(opt => ({
          text: opt.text,
          is_correct: opt.is_correct
        })),
        score: score,
        topic_id: selectedTopic,
        section_id: selectedSection,
        language: primaryLanguage,
        translations: translationsObj
      };

      if (editingQuestion) {
        await axios.put(`${API}/assessments/questions/${editingQuestion.question_id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Question updated successfully');
      } else {
        await axios.post(`${API}/assessments/questions`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Question created successfully');
      }
      
      setShowAddModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save question');
    }
  };

  const handleDelete = async (questionId) => {
    if (!questionId) {
      toast.error('Question ID is missing');
      console.error('No question ID provided to handleDelete');
      return;
    }

    if (!confirm('Are you sure you want to delete this question?')) return;

    try {
      const token = localStorage.getItem('token');
      console.log('Deleting question with ID:', questionId);
      console.log('API URL:', `${API}/assessments/questions/${questionId}`);
      
      const response = await axios.delete(`${API}/assessments/questions/${questionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Delete response:', response.data);
      toast.success('Question deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Delete error:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to delete question';
      toast.error(errorMessage);
    }
  };

  const handleEdit = (question) => {
    console.log('Editing question:', question);
    console.log('Topic ID:', question.topic_id);
    console.log('Subchapter ID:', question.section_id);
    console.log('Translations:', question.translations);
    
    setEditingQuestion(question);
    setFormStep(7); // Show all steps when editing
    setSelectedTopic(question.topic_id || '');
    setSelectedSection(question.section_id || '');
    setQuestionText(question.question_text);
    setOptions(question.options || [{ text: '', is_correct: false }, { text: '', is_correct: false }]);
    setAnswerType(question.question_type);
    setScore(question.score || 1);
    setPrimaryLanguage(question.language || 'English');
    
    // Load translations if available
    if (question.translations && Object.keys(question.translations).length > 1) {
      const languages = Object.keys(question.translations);
      const secondaryLangs = languages
        .filter(lang => lang !== (question.language || 'English'))
        .map(lang => ({
          language: lang,
          questionText: question.translations[lang].question_text || '',
          options: question.translations[lang].options || [{ text: '', is_correct: false }, { text: '', is_correct: false }]
        }));
      setSecondaryLanguages(secondaryLangs);
    }
    
    setShowAddModal(true);
  };

  // Filter questions
  const filteredQuestions = (Array.isArray(questions) ? questions : []).filter(q => {
    if (topicFilter && q.topic_id !== topicFilter) return false;
    if (sectionFilter && q.section_id !== sectionFilter) return false;
    // Language filter removed as per requirement
    if (searchText && !q.question_text.toLowerCase().includes(searchText.toLowerCase())) return false;
    return true;
  });

  // Get unique sections from selected topic
  const getTopicSections = () => {
    if (!selectedTopic) return [];
    // Find topic by either training_topic_id or id
    const topic = trainingTopics.find(t => 
      t.training_topic_id === selectedTopic || t.id === selectedTopic
    );
    console.log('Selected Topic ID:', selectedTopic);
    console.log('Found Topic:', topic);
    console.log('Topic Sections:', topic?.sections);
    
    // Check different possible structures
    if (topic?.sections && Array.isArray(topic.sections)) {
      return topic.sections;
    }
    if (topic?.training_sections && Array.isArray(topic.training_sections)) {
      return topic.training_sections;
    }
    return [];
  };

  // Get all unique sections from all topics for the filter dropdown
  const getAllSections = () => {
    const sections = [];
    trainingTopics.forEach(topic => {
      if (topic.sections && Array.isArray(topic.sections)) {
        topic.sections.forEach(section => {
          if (section.section_title && !sections.includes(section.section_title)) {
            sections.push(section.section_title);
          }
        });
      }
    });
    return sections;
  };

  return (
    <AdminLayout>
      <div className="p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-gray-900">QUESTION BANK</h1>
          <Button
            onClick={() => {
              resetForm();
              setShowAddModal(true);
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Question
          </Button>
        </div>

        {/* Filters */}
        <Card className="p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="Search questions..."
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Topic Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Chapter</label>
              <select
                value={topicFilter}
                onChange={(e) => setTopicFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Chapters</option>
                {trainingTopics.map(topic => (
                  <option key={topic.id || topic.training_topic_id} value={topic.id || topic.training_topic_id}>
                    {topic.title}
                  </option>
                ))}
              </select>
            </div>

            {/* Subchapter Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subchapter</label>
              <select
                value={sectionFilter}
                onChange={(e) => setSectionFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Subchapters</option>
                {getAllSections().map((section, idx) => (
                  <option key={idx} value={section}>
                    {section}
                  </option>
                ))}
              </select>
            </div>

            {/* Language Filter Removed - No longer needed */}
          </div>
        </Card>

        {/* Questions Table */}
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Question Text</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Chapter</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subchapter</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Language</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                      Loading questions...
                    </td>
                  </tr>
                ) : filteredQuestions.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                      No questions found. Click "Add Question" to create one.
                    </td>
                  </tr>
                ) : (
                  filteredQuestions.map((question) => (
                    <tr key={question.question_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">{question.question_text}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {trainingTopics.find(t => (t.id || t.training_topic_id) === question.topic_id)?.title || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{question.section_id || '-'}</td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {question.translations && Object.keys(question.translations).length > 0 ? (
                            Object.keys(question.translations).map(lang => (
                              <span
                                key={lang}
                                className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded"
                              >
                                {lang.substring(0, 2).toUpperCase()}
                              </span>
                            ))
                          ) : (
                            <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded">
                              {(question.language || 'English').substring(0, 2).toUpperCase()}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{question.score || 1}</td>
                      <td className="px-6 py-4 text-sm">
                        <div className="flex items-center space-x-2">
                          <Button
                            onClick={() => handleEdit(question)}
                            variant="outline"
                            size="sm"
                            data-tooltip="Edit Question"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            onClick={() => handleDelete(question.question_id)}
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            data-tooltip="Delete Question"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Add/Edit Question Modal */}
        <Dialog open={showAddModal} onOpenChange={(open) => {
          setShowAddModal(open);
          if (!open) resetForm();
        }}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden" hideClose={true}>
            <div className="flex items-center justify-between mb-4 flex-shrink-0">
              <h2 className="text-2xl font-bold">
                {editingQuestion ? 'Edit Question' : 'Add Question'}
              </h2>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto min-h-0">
            {/* Progress Steps */}
            <div className="flex items-center justify-between mb-6">
              {[1, 2, 3, 4, 5, 6, 7].map((step) => (
                <div key={step} className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    formStep >= step ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {step}
                  </div>
                  {step < 7 && (
                    <div className={`w-12 h-1 ${formStep > step ? 'bg-blue-600' : 'bg-gray-200'}`} />
                  )}
                </div>
              ))}
            </div>

            <div className="space-y-6">
              {/* Step 1: Select Topic */}
              {formStep >= 1 && (
                <div className="border rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Step 1: Select Topic
                  </label>
                  <select
                    value={selectedTopic}
                    onChange={(e) => setSelectedTopic(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">Choose a chapter...</option>
                    {trainingTopics.map(topic => (
                      <option key={topic.id || topic.training_topic_id} value={topic.id || topic.training_topic_id}>
                        {topic.title}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Step 2: Select Subchapter */}
              {formStep >= 2 && selectedTopic && (
                <div className="border rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Step 2: Select Section
                    {selectedSubchapter && (
                      <span className="ml-2 text-xs text-blue-600">
                        (Currently: {selectedSection})
                      </span>
                    )}
                  </label>
                  {getTopicSections().length > 0 ? (
                    <select
                      value={selectedSection}
                      onChange={(e) => {
                        console.log('Subchapter changed to:', e.target.value);
                        setSelectedSection(e.target.value);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Choose a subchapter...</option>
                      {getTopicSections().map((section, idx) => (
                        <option key={idx} value={section.section_title}>
                          {section.section_title}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <p className="text-sm text-yellow-800">
                        No sections available for this chapter. The selected training chapter doesn't have any sections defined.
                      </p>
                      <p className="text-xs text-yellow-600 mt-2">
                        You can either create sections for this chapter or select "General" as a placeholder.
                      </p>
                      <input
                        type="text"
                        value={selectedSection}
                        onChange={(e) => setSelectedSection(e.target.value)}
                        placeholder="Enter subchapter name (e.g., General)"
                        className="w-full mt-3 px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Step 3: Question Text */}
              {formStep >= 3 && selectedSubchapter && (
                <div className="border rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Step 3: Enter Question Text
                  </label>
                  <textarea
                    value={questionText}
                    onChange={(e) => setQuestionText(e.target.value)}
                    placeholder="Enter your question here..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    rows={3}
                  />
                </div>
              )}

              {/* Step 4: Options */}
              {formStep >= 4 && questionText && (
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Step 4: Add Options
                    </label>
                    <Button onClick={handleAddOption} size="sm" variant="outline">
                      <Plus className="w-4 h-4 mr-1" />
                      Add Option
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {options.map((option, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <input
                          type={answerType === 'mcq_single' ? 'radio' : 'checkbox'}
                          checked={option.is_correct}
                          onChange={(e) => handleOptionChange(index, 'is_correct', e.target.checked)}
                          className="w-4 h-4"
                        />
                        <input
                          type="text"
                          value={option.text}
                          onChange={(e) => handleOptionChange(index, 'text', e.target.value)}
                          placeholder={`Option ${index + 1}`}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                        />
                        {options.length > 2 && (
                          <Button
                            onClick={() => handleRemoveOption(index)}
                            size="sm"
                            variant="outline"
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 5: Answer Type */}
              {formStep >= 5 && options.every(o => o.text) && (
                <div className="border rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Step 5: Answer Type
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center space-x-2">
                      <input
                        type="radio"
                        checked={answerType === 'mcq_single'}
                        onChange={() => setAnswerType('mcq_single')}
                        className="w-4 h-4"
                      />
                      <span>Single Correct Answer</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="radio"
                        checked={answerType === 'mcq_multiple'}
                        onChange={() => setAnswerType('mcq_multiple')}
                        className="w-4 h-4"
                      />
                      <span>Multiple Correct Answers</span>
                    </label>
                  </div>
                </div>
              )}

              {/* Step 6: Score */}
              {formStep >= 6 && (
                <div className="border rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Step 6: Score
                  </label>
                  <input
                    type="number"
                    value={score}
                    onChange={(e) => setScore(parseInt(e.target.value) || 1)}
                    min="1"
                    className="w-32 px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              )}

              {/* Step 7: Primary Language */}
              {formStep >= 7 && (
                <div className="border rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Step 7: Primary Language
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={showPrimaryLanguageDropdown ? primaryLanguageSearch : primaryLanguage}
                      onChange={(e) => setPrimaryLanguageSearch(e.target.value)}
                      onFocus={() => setShowPrimaryLanguageDropdown(true)}
                      placeholder="Search language..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    {showPrimaryLanguageDropdown && (
                      <>
                        <div 
                          className="fixed inset-0 z-30"
                          onClick={() => {
                            setShowPrimaryLanguageDropdown(false);
                            setPrimaryLanguageSearch('');
                          }}
                        />
                        <div className="absolute z-40 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {availableLanguages
                            .filter(lang => lang.toLowerCase().includes(primaryLanguageSearch.toLowerCase()))
                            .map(lang => (
                              <div
                                key={lang}
                                onClick={() => {
                                  setPrimaryLanguage(lang);
                                  setShowPrimaryLanguageDropdown(false);
                                  setPrimaryLanguageSearch('');
                                }}
                                className={`px-3 py-2 cursor-pointer hover:bg-blue-50 text-sm ${
                                  primaryLanguage === lang ? 'bg-blue-100 font-medium' : ''
                                }`}
                              >
                                {lang}
                              </div>
                            ))}
                        </div>
                      </>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-2">
                    Selected primary language: <span className="font-medium">{primaryLanguage}</span>
                  </p>
                </div>
              )}

              {/* Add Secondary Language Subchapter */}
              {formStep >= 7 && (
                <div className="mt-4 flex justify-center">
                  <Button
                    onClick={() => {
                      const usedLanguages = [primaryLanguage, ...secondaryLanguages.map(sl => sl.language)];
                      const availableLangs = availableLanguages.filter(lang => !usedLanguages.includes(lang));
                      if (availableLangs.length > 0) {
                        setSecondaryLanguages([
                          ...secondaryLanguages,
                          {
                            language: '',
                            questionText: '',
                            options: options.map(opt => ({ text: '', is_correct: opt.is_correct }))
                          }
                        ]);
                      } else {
                        toast.error('All available languages have been added');
                      }
                    }}
                    variant="outline"
                    className="border-2 border-dashed border-blue-400 text-blue-600 hover:bg-blue-50"
                    disabled={secondaryLanguages.length >= availableLanguages.length - 1}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Language (Optional)
                  </Button>
                </div>
              )}

              {/* Secondary Languages Form - Multiple Languages Support */}
              {formStep >= 7 && secondaryLanguages.map((secLang, langIndex) => (
                <div key={langIndex} className="mt-6 border-2 border-blue-200 rounded-lg p-6 bg-blue-50">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Secondary Language {langIndex + 1}
                    </h3>
                    <button
                      onClick={() => {
                        setSecondaryLanguages(secondaryLanguages.filter((_, i) => i !== langIndex));
                      }}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      Remove
                    </button>
                  </div>

                  {/* Secondary Language Selection */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Language
                    </label>
                    <select
                      value={secLang.language}
                      onChange={(e) => {
                        const updated = [...secondaryLanguages];
                        updated[langIndex].language = e.target.value;
                        setSecondaryLanguages(updated);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                    >
                      <option value="">Choose a language...</option>
                      {availableLanguages
                        .filter(lang => 
                          lang !== primaryLanguage && 
                          !secondaryLanguages.some((sl, i) => i !== langIndex && sl.language === lang)
                        )
                        .map(lang => (
                          <option key={lang} value={lang}>{lang}</option>
                        ))}
                    </select>
                  </div>

                  {/* Secondary Question Text */}
                  {secLang.language && (
                    <>
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Question Text ({secLang.language})
                        </label>
                        <textarea
                          value={secLang.questionText}
                          onChange={(e) => {
                            const updated = [...secondaryLanguages];
                            updated[langIndex].questionText = e.target.value;
                            setSecondaryLanguages(updated);
                          }}
                          placeholder={`Enter question in ${secLang.language}...`}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          rows={3}
                        />
                      </div>

                      {/* Secondary Options */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Answer Options ({secLang.language})
                          <span className="ml-2 text-xs text-gray-500">
                            (Correct answers auto-synced from primary language)
                          </span>
                        </label>
                        <div className="space-y-2">
                          {secLang.options.map((option, optIndex) => (
                            <div key={optIndex} className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                checked={option.is_correct}
                                disabled
                                className="w-4 h-4 opacity-60 cursor-not-allowed"
                                data-tooltip="Correct answers are synced from primary language"
                              />
                              <input
                                type="text"
                                value={option.text}
                                onChange={(e) => {
                                  const updated = [...secondaryLanguages];
                                  updated[langIndex].options[optIndex].text = e.target.value;
                                  setSecondaryLanguages(updated);
                                }}
                                placeholder={`Option ${optIndex + 1} in ${secLang.language}`}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                              />
                            </div>
                          ))}
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          ✓ Option count and correct answers are automatically synced with primary language
                        </p>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-6 pt-4 border-t">
              <Button
                onClick={() => setFormStep(Math.max(1, formStep - 1))}
                variant="outline"
                disabled={formStep === 1}
              >
                Previous
              </Button>
              <div className="flex space-x-2">
                {formStep < 7 ? (
                  <Button
                    onClick={() => setFormStep(formStep + 1)}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    Next
                  </Button>
                ) : (
                  <>
                    <Button onClick={() => setShowAddModal(false)} variant="outline">
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSave}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      Save Question
                    </Button>
                  </>
                )}
              </div>
            </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default QuestionBankPageNew;
